import React from 'react';
import { ThreadMessage } from '../../../../types/threadMessage';
import { Popover, Transition } from '@headlessui/react';
import { FiInfo } from 'react-icons/fi';
import IconWrapper from '../../../IconWrapper';

export interface ChatBubbleProps {
  message: ThreadMessage;
  align: 'left' | 'right';
  showMeta?: boolean;
  fallbackCustomerName?: string;
  renderAction?: (message: ThreadMessage) => React.ReactNode;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({ message, align, showMeta = true, renderAction, fallbackCustomerName }) => {
  const isAgent = message.author_type === 'agent' || message.message_type === 'agent_response' || message.author_type === 'system';
  const authorName = message.author_name || (isAgent ? 'Support Agent' : fallbackCustomerName || 'Customer');
  const timeAgo = (iso: string) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const bubbleBase = 'rounded-2xl shadow-sm px-4 py-3 max-w-[70%]';
  const customerBubble = `${bubbleBase} bg-white text-figma-text-primary rounded-tl-sm`;
  const agentBubble = `${bubbleBase} bg-[#4342FF] text-white rounded-tr-sm`;

  return (
    <div className={`w-full flex items-start mb-4 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {isAgent ? (
        <img
          src="/agent-logo.svg"
          alt="Agent"
          className={`w-10 h-10 ${align === 'right' ? 'ml-3' : 'mr-3'} flex-shrink-0`}
        />
      ) : (
        <div className={`w-10 h-10 ${align === 'right' ? 'ml-3' : 'mr-3'} rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0`}>
          <span className="text-sm font-semibold text-gray-700">
            {authorName.split(' ').map((n: string) => n[0]).join('').toUpperCase() || 'C'}
          </span>
        </div>
      )}

      <div className={`flex-1 ${align === 'right' ? 'items-end text-right' : ''}`}>
        {showMeta && (
          <div className={`flex items-center ${align === 'right' ? 'justify-end' : ''} gap-2 mb-1`}>
            <span className={`font-semibold text-figma-base ${align === 'right' ? 'text-[#262628]' : 'text-figma-text-primary'}`}>{authorName}</span>
            <span className="text-figma-xs text-figma-text-muted">{timeAgo(message.sent_at)}</span>
            {(message.external_id || message.author_type || message.message_type) && (
              <Popover className="relative">
                <Popover.Button className="focus:outline-none" aria-label="Message info">
                  <IconWrapper icon={FiInfo} className="w-4 h-4 text-gray-400 hover:text-figma-text-primary" />
                </Popover.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel className={`absolute z-10 ${align === 'right' ? 'right-0' : 'left-0'} mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700`}>
                    {message.external_id && (
                      <div className="mb-1">
                        <span className="font-semibold">External ID:</span> {message.external_id}
                      </div>
                    )}
                    {message.author_type && (
                      <div className="mb-1">
                        <span className="font-semibold">Author Type:</span> {message.author_type}
                      </div>
                    )}
                    {message.message_type && (
                      <div>
                        <span className="font-semibold">Message Type:</span> {message.message_type}
                      </div>
                    )}
                  </Popover.Panel>
                </Transition>
              </Popover>
            )}
            {renderAction && (
              <div className="ml-2">{renderAction(message)}</div>
            )}
          </div>
        )}
        <div className={`${align === 'right' ? 'ml-auto' : ''} ${isAgent ? agentBubble : customerBubble}`}>
          <p className={`text-figma-base leading-relaxed ${isAgent ? 'text-white' : 'text-figma-text-primary'}`}>{message.content}</p>
        </div>
      </div>
    </div>
  );
};

export default ChatBubble;
